import React, { useState } from 'react';
import { Form, Input, Button, Typography, message, Alert } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import customerApi from '../services/customerApi';
import { customerAuthService } from '../services/customerAuth';

const { Title, Text } = Typography;

export const CustomerChangePassword: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const onFinish = async (values: { current_password: string; new_password: string; new_password_confirmation: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerApi.post('/customer/change-password', values);
      // 更新 localStorage 中的 customer 对象和 must_change_password 标记
      if (response.data?.data?.customer) {
        localStorage.setItem('customer', JSON.stringify(response.data.data.customer));
      }
      localStorage.setItem('customer_must_change_password', '0');
      message.success('Password changed successfully, you can now use the system.');
      navigate('/customer/dashboard');
    } catch (error: any) {
      console.error('Change password error:', error);
      let errorMessage = 'Failed to change password';
      if (error.response) {
        errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = () => {
    setError('Please fill in all required fields correctly');
  };

  // 如果已经不需要改密码了，直接去 dashboard
  if (!customerAuthService.mustChangePassword()) {
    navigate('/customer/dashboard');
  }

  return (
    <div style={{ 
      height: '100vh', 
      width: '100vw',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: '#f5f5f5',
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        maxWidth: '450px',
        width: '100%',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2} style={{ marginBottom: '8px', color: '#262626' }}>
            Change Password
          </Title>
          <Text style={{ color: '#8c8c8c', fontSize: '16px' }}>
            For security, please change your password before using the system.
          </Text>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: '24px' }}
          />
        )}

        <Form
          name="customer-change-password"
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="current_password"
            label="Current Password"
            rules={[{ required: true, message: 'Please enter your current password' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter current password"
              style={{ height: '48px', fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item
            name="new_password"
            label="New Password"
            rules={[{ required: true, message: 'Please enter your new password' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter new password"
              style={{ height: '48px', fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item
            name="new_password_confirmation"
            label="Confirm New Password"
            dependencies={['new_password']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm new password"
              style={{ height: '48px', fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ 
                width: '100%', 
                height: '48px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              Change Password
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};



